import React, { useState } from 'react';
import {
  LayoutDashboard, Search, ClipboardList, Users, Receipt, Wallet,
  Car, Calendar, Bell, Package, BarChart3, FileText, Edit3, FileCog, Settings, LogOut, HandCoins, AlertTriangle,
  ReceiptEuro, CalendarClock, Calculator, BookOpen, Scale, Send, ChevronRight, Landmark, CalendarCheck, Lock, Stethoscope, ShoppingCart, Megaphone,
  PackageSearch, Boxes, Wrench, PackagePlus, Recycle, History, Globe, Rocket, Sparkles, ShieldCheck, Disc3, Truck, ShieldQuestion, Repeat, MessageSquare, Plug
} from 'lucide-react';
import { useT } from '../context/LanguageContext';
import { useData } from '../context/DataContext';

const buildNav = (t) => [
  { key: 'dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
  { key: 'aandeslag', label: t('nav.getstarted', 'Aan de slag'), icon: Rocket },
  { key: 'integraties', label: t('nav.integrations', 'Integraties & accounts'), icon: Plug },
  { key: 'zoeken', label: t('nav.search'), icon: Search },
  { key: 'werkkaart', label: t('nav.workcard'), icon: ClipboardList },
  {
    key: 'ai', label: t('nav.ai', 'AI'), icon: Sparkles, children: [
      { key: 'diagnose', label: t('nav.diagnose', 'AI Diagnose'), icon: Stethoscope },
      { key: 'ai-foutcode', label: t('nav.faultcode', 'AI Foutcode-uitleg'), icon: AlertTriangle },
      { key: 'ai-offerte', label: t('nav.quoteassist', 'AI Offerte-assistent'), icon: FileText },
      { key: 'advertentie', label: t('nav.admaker', 'Advertentie maken'), icon: Megaphone },
      { key: 'oh-waardebepaling', label: t('nav.oh_value', 'AI Waardebepaling'), icon: Calculator },
    ],
  },
  { key: 'klanten', label: t('nav.customers'), icon: Users },
  { key: 'bonnen', label: t('nav.receipts'), icon: Receipt },
  { key: 'betalingen', label: t('nav.payments'), icon: Wallet },
  {
    key: 'boekhouden', label: t('nav.accounting', 'Boekhouden'), icon: Calculator, children: [
      { key: 'debiteuren', label: t('nav.debtors', 'Debiteurenbeheer'), icon: CalendarClock },
      { key: 'kosten', label: t('nav.expenses', 'Kosten / Inkoopfacturen'), icon: ReceiptEuro },
      { key: 'herhaalfacturen', label: t('nav.recurring', 'Herhaalfacturen'), icon: Repeat },
      { key: 'vasteactiva', label: t('nav.assets', 'Vaste activa'), icon: Boxes },
      { key: 'efacturatie', label: t('nav.einvoicing', 'E-facturatie & SEPA'), icon: FileText },
      { key: 'herinneringen', label: t('nav.reminders', 'Automatische herinneringen'), icon: Send },
      { key: 'boekhouding', label: t('nav.vatprofit', 'BTW & Winst'), icon: Receipt },
      { key: 'grootboek', label: t('nav.ledger', 'Grootboek'), icon: BookOpen },
      { key: 'balans', label: t('nav.balance', 'Balans'), icon: Scale },
      { key: 'bankimport', label: t('nav.bankimport', 'Bankimport'), icon: Landmark },
    ],
  },
  { key: 'autohandel', label: t('nav.cartrade'), icon: Car },
  {
    key: 'onderdelenhandel', label: t('nav.partstrade', 'Onderdelenhandel'), icon: PackageSearch, children: [
      { key: 'oh-voorraad', label: t('nav.oh_stock', 'Voorraad'), icon: Boxes },
      { key: 'oh-gebruikt', label: t('nav.oh_used', 'Gebruikte onderdelen'), icon: Wrench },
      { key: 'oh-nieuw', label: t('nav.oh_new', 'Nieuwe onderdelen'), icon: PackagePlus },
      { key: 'oh-sloop', label: t('nav.oh_scrap', "Sloopauto's"), icon: Recycle },
      { key: 'oh-advertenties', label: t('nav.oh_ads', 'Advertenties'), icon: Megaphone },
      { key: 'oh-waardebepaling', label: t('nav.oh_value', 'Waardebepaling'), icon: Calculator },
      { key: 'oh-historie', label: t('nav.oh_history', 'Verkoophistorie'), icon: History },
    ],
  },
  { key: 'showroom', label: t('nav.showroom', 'Showroom'), icon: Globe },
  {
    key: 'inkoop', label: t('nav.purchasing', 'Inkoop'), icon: ShoppingCart, children: [
      { key: 'leveranciers', label: t('nav.suppliers', 'Leveranciers'), icon: Truck },
      { key: 'inkooporders', label: t('nav.purchaseorders', 'Inkooporders'), icon: ShoppingCart },
      { key: 'garantieclaims', label: t('nav.warranty', 'Garantieclaims'), icon: ShieldQuestion },
    ]
  },
  { key: 'planning', label: t('nav.planning'), icon: Calendar },
  { key: 'apk', label: t('nav.apk', 'APK & Onderhoud'), icon: ShieldCheck },
  { key: 'smsherinneringen', label: t('nav.smsreminders', 'Afspraakherinneringen'), icon: MessageSquare },
  { key: 'bandenhotel', label: t('nav.tyrehotel', 'Bandenhotel'), icon: Disc3 },
  { key: 'onlineafspraken', label: t('nav.onlinebooking', 'Online afspraken'), icon: CalendarCheck },
  { key: 'meldingen', label: t('nav.alerts'), icon: Bell },
  { key: 'onderdelen', label: t('nav.parts'), icon: Package },
  { key: 'rapportage', label: t('nav.reporting'), icon: BarChart3 },
  { key: 'verrekenen', label: t('nav.settle'), icon: HandCoins },
  { key: 'documenten', label: t('nav.documents'), icon: FileText },
  { key: 'bonlayout', label: t('nav.layout'), icon: Edit3 },
  { key: 'gegevens', label: t('nav.garage'), icon: FileCog },
  { key: 'instellingen', label: t('nav.settings'), icon: Settings },
  { key: 'info', label: t('nav.whatsnew', "Wat's nieuw"), icon: Rocket },
];

export default function Sidebar({ activePage, onNavigate, user, onLogout, mobileOpen, onCloseMobile }) {
  const { t } = useT();
  const { planning, stats, gegevens } = useData();
  const aanvragen = (planning || []).filter(p => p.status === 'Aangevraagd').length;
  const licensed = ['PRO', 'ENTERPRISE', 'LIFETIME'].includes(stats?.plan);
  const GATED_KEYS = ['ai', 'boekhouden', 'onlineafspraken', 'onderdelenhandel', 'showroom', 'smsherinneringen'];
  const navItems = buildNav(t);
  const [flyout, setFlyout] = useState(null); // { key, top }

  const go = (key) => { onNavigate(key); setFlyout(null); if (onCloseMobile) onCloseMobile(); };
  const flyoutGroup = flyout ? navItems.find(i => i.key === flyout.key) : null;

  return (
    <aside className={`cs-sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
      <div className="cs-sidebar-title">
        <h1>MonteurCentraal</h1>
        <p>{gegevens?.naam || user?.garageNaam || 'Garage management'}</p>
      </div>
      <nav className="cs-nav">
        {navItems.map(item => {
          const Icon = item.icon;
          if (item.children) {
            const childActive = item.children.some(c => c.key === activePage);
            const isOpen = flyout?.key === item.key;
            return (
              <button
                key={item.key}
                data-testid={`nav-${item.key}`}
                className={`cs-nav-btn ${childActive ? 'active' : ''}`}
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setFlyout(prev => prev?.key === item.key ? null : { key: item.key, top: r.top });
                }}
              >
                <Icon size={16} strokeWidth={2} />
                <span>{item.label}</span>
                {GATED_KEYS.includes(item.key) && !licensed && (
                  <Lock size={13} className="cs-nav-lock" data-testid={`nav-${item.key}-lock`} style={{ marginLeft: 'auto' }} />
                )}
                <ChevronRight size={15} style={{ marginLeft: GATED_KEYS.includes(item.key) && !licensed ? 6 : 'auto', transition: 'transform 150ms ease', transform: isOpen ? 'rotate(90deg)' : 'none' }} />
              </button>
            );
          }
          return (
            <button
              key={item.key}
              data-testid={`nav-${item.key}`}
              className={`cs-nav-btn ${activePage === item.key ? 'active' : ''}`}
              onClick={() => go(item.key)}
            >
              <Icon size={16} strokeWidth={2} />
              <span>{item.label}</span>
              {item.key === 'planning' && aanvragen > 0 && (
                <span className="cs-nav-badge" data-testid="nav-planning-badge">{aanvragen}</span>
              )}
              {GATED_KEYS.includes(item.key) && !licensed && (
                <Lock size={13} className="cs-nav-lock" data-testid={`nav-${item.key}-lock`} style={{ marginLeft: 'auto' }} />
              )}
            </button>
          );
        })}
      </nav>

      {flyoutGroup && (
        <>
          <div className="cs-flyout-backdrop" onClick={() => setFlyout(null)} data-testid="flyout-backdrop" />
          <div className="cs-flyout" style={{ top: Math.min(flyout.top, window.innerHeight - 320) }} data-testid="boekhouden-flyout">
            <div className="cs-flyout-title">{flyoutGroup.label}</div>
            {flyoutGroup.children.map(c => {
              const CI = c.icon;
              return (
                <button
                  key={c.key}
                  data-testid={`nav-${c.key}`}
                  className={`cs-nav-btn ${activePage === c.key ? 'active' : ''}`}
                  onClick={() => go(c.key)}
                >
                  <CI size={15} strokeWidth={2} />
                  <span>{c.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {user && (
        <div className="cs-sidebar-user">
          <div className="cs-user-info">
            <div className="cs-user-avatar">{(user.naam || user.email || '?').charAt(0).toUpperCase()}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="cs-user-name">{user.naam || user.email}</div>
              <div className="cs-user-role">{user.role === 'owner' ? 'Eigenaar' : 'Medewerker'}</div>
            </div>
            <button className="cs-user-logout" onClick={onLogout} title="Uitloggen">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      )}
      <style>{`
        .cs-sidebar-user { padding: 12px 10px; border-top: 1px solid rgba(255,255,255,0.06); }
        .cs-user-info { display: flex; align-items: center; gap: 10px; padding: 6px; border-radius: 6px; }
        .cs-user-avatar { width: 32px; height: 32px; border-radius: 50%; background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%); display: flex; align-items: center; justify-content: center; color: #fff; font-weight: 600; font-size: 14px; flex-shrink: 0; }
        .cs-user-name { font-size: 12.5px; color: #fff; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .cs-user-role { font-size: 11px; color: #94a3b8; }
        .cs-user-logout { background: transparent; border: none; color: #94a3b8; cursor: pointer; padding: 6px; border-radius: 4px; display: flex; align-items: center; }
        .cs-user-logout:hover { background: rgba(255,255,255,0.08); color: #fff; }
        .cs-flyout-backdrop { position: fixed; inset: 0; z-index: 90; }
        .cs-flyout { position: fixed; left: calc(var(--cs-sidebar-width) + 4px); z-index: 95; background: var(--cs-sidebar); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 8px; min-width: 250px; box-shadow: 10px 12px 34px rgba(0,0,0,0.4); animation: flyoutIn 160ms ease; }
        .cs-flyout .cs-nav-btn { color: #cbd5e1; }
        .cs-flyout-title { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.6px; color: #94a3b8; padding: 4px 12px 8px; }
        .cs-nav-badge { margin-left: auto; background: #ef4444; color: #fff; font-size: 10.5px; font-weight: 700; min-width: 19px; height: 19px; padding: 0 6px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; box-shadow: 0 0 0 2px rgba(239,68,68,0.25); animation: badgePulse 2s ease-in-out infinite; }
        .cs-nav-lock { color: #f59e0b; opacity: 0.85; flex-shrink: 0; }
        @keyframes badgePulse { 0%, 100% { box-shadow: 0 0 0 2px rgba(239,68,68,0.25); } 50% { box-shadow: 0 0 0 4px rgba(239,68,68,0.12); } }
        @keyframes flyoutIn { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>
    </aside>
  );
}
